import React, { useState } from 'react';
import { Car, FileText, User } from 'lucide-react';
import toast from 'react-hot-toast';

const MatriculaInicial: React.FC = () => {
  const [formData, setFormData] = useState({
    // Datos del vehículo
    marca: '',
    modelo: '',
    año: '',
    color: '',
    tipoServicio: '',
    numeroMotor: '',
    numeroChasis: '',
    cilindrada: '',
    combustible: '',
    
    // Datos del propietario
    tipoDocumento: '',
    numeroDocumento: '',
    nombre: '',
    apellido: '',
    telefono: '',
    email: '',
    direccion: '',
    
    // Datos adicionales
    valorComercial: '',
    observaciones: ''
  });

  const marcas = [
    'Chevrolet', 'Renault', 'Nissan', 'Toyota', 'Hyundai', 'Kia', 'Mazda', 'Ford',
    'Volkswagen', 'BMW', 'Mercedes-Benz', 'Audi', 'Honda', 'Mitsubishi', 'Suzuki'
  ];

  const colores = [
    'Blanco', 'Negro', 'Gris', 'Plateado', 'Azul', 'Rojo', 'Verde', 'Amarillo',
    'Naranja', 'Marrón', 'Dorado', 'Beige'
  ];

  const tiposServicio = [
    'Particular', 'Público', 'Oficial', 'Diplomático', 'Comercial'
  ];

  const tiposCombustible = [
    'Gasolina', 'Diesel', 'Gas Natural', 'Eléctrico', 'Híbrido'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!formData.marca || !formData.modelo || !formData.año) {
      toast.error('Por favor completa los datos del vehículo');
      return;
    }
    
    if (!formData.tipoDocumento || !formData.numeroDocumento || !formData.nombre) {
      toast.error('Por favor completa los datos del propietario');
      return;
    }
    
    toast.success('Matrícula inicial procesada exitosamente');
    console.log('Datos del formulario:', formData);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Matrícula Inicial de Vehículo</h1>
        <p className="text-gray-600">Registra un vehículo nuevo en el sistema municipal</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Datos del Vehículo */}
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Car className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">Datos del Vehículo</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="label">Marca *</label>
              <select
                name="marca"
                value={formData.marca}
                onChange={handleInputChange}
                className="input"
                required
              >
                <option value="">Seleccione marca</option>
                {marcas.map(marca => (
                  <option key={marca} value={marca}>{marca}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="label">Modelo *</label>
              <input
                type="text"
                name="modelo"
                value={formData.modelo}
                onChange={handleInputChange}
                className="input"
                placeholder="Ej: Spark"
                required
              />
            </div>
            
            <div>
              <label className="label">Año *</label>
              <input
                type="number"
                name="año"
                value={formData.año}
                onChange={handleInputChange}
                className="input"
                placeholder="2024"
                min="1990"
                max="2025"
                required
              />
            </div>
            
            <div>
              <label className="label">Color *</label>
              <select
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                className="input"
                required
              >
                <option value="">Seleccione color</option>
                {colores.map(color => (
                  <option key={color} value={color}>{color}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="label">Tipo de Servicio *</label>
              <select
                name="tipoServicio"
                value={formData.tipoServicio}
                onChange={handleInputChange}
                className="input"
                required
              >
                <option value="">Seleccione tipo</option>
                {tiposServicio.map(tipo => (
                  <option key={tipo} value={tipo}>{tipo}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="label">Cilindrada</label>
              <input
                type="text"
                name="cilindrada"
                value={formData.cilindrada}
                onChange={handleInputChange}
                className="input"
                placeholder="Ej: 1.4L"
              />
            </div>
            
            <div>
              <label className="label">Combustible</label>
              <select
                name="combustible"
                value={formData.combustible}
                onChange={handleInputChange}
                className="input"
              >
                <option value="">Seleccione combustible</option>
                {tiposCombustible.map(tipo => (
                  <option key={tipo} value={tipo}>{tipo}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="label">Número de Motor</label>
              <input
                type="text"
                name="numeroMotor"
                value={formData.numeroMotor}
                onChange={handleInputChange}
                className="input"
                placeholder="Número de motor"
              />
            </div>
            
            <div>
              <label className="label">Número de Chasis</label>
              <input
                type="text"
                name="numeroChasis"
                value={formData.numeroChasis}
                onChange={handleInputChange}
                className="input"
                placeholder="Número de chasis"
              />
            </div>
          </div>
        </div>

        {/* Datos del Propietario */}
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-6">
            <User className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">Datos del Propietario</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="label">Tipo de Documento *</label>
              <select
                name="tipoDocumento"
                value={formData.tipoDocumento}
                onChange={handleInputChange}
                className="input"
                required
              >
                <option value="">Seleccione tipo</option>
                <option value="CC">Cédula de Ciudadanía</option>
                <option value="CE">Cédula de Extranjería</option>
                <option value="NIT">NIT</option>
                <option value="TI">Tarjeta de Identidad</option>
              </select>
            </div>
            
            <div>
              <label className="label">Número de Documento *</label>
              <input
                type="text"
                name="numeroDocumento"
                value={formData.numeroDocumento}
                onChange={handleInputChange}
                className="input"
                placeholder="Número de documento"
                required
              />
            </div>
            
            <div>
              <label className="label">Nombre *</label>
              <input
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                className="input"
                placeholder="Nombre"
                required
              />
            </div>
            
            <div>
              <label className="label">Apellido *</label>
              <input
                type="text"
                name="apellido"
                value={formData.apellido}
                onChange={handleInputChange}
                className="input"
                placeholder="Apellido"
                required
              />
            </div>
            
            <div>
              <label className="label">Teléfono</label>
              <input
                type="tel"
                name="telefono"
                value={formData.telefono}
                onChange={handleInputChange}
                className="input"
                placeholder="Teléfono"
              />
            </div>
            
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="input"
                placeholder="Email"
              />
            </div>
            
            <div className="md:col-span-2 lg:col-span-3">
              <label className="label">Dirección</label>
              <input
                type="text"
                name="direccion"
                value={formData.direccion}
                onChange={handleInputChange}
                className="input"
                placeholder="Dirección completa"
              />
            </div>
          </div>
        </div>

        {/* Datos Adicionales */}
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-6">
            <FileText className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">Información Adicional</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">Valor Comercial</label>
              <input
                type="number"
                name="valorComercial"
                value={formData.valorComercial}
                onChange={handleInputChange}
                className="input"
                placeholder="Valor en pesos"
                min="0"
              />
            </div>
            
            <div>
              <label className="label">Observaciones</label>
              <textarea
                name="observaciones"
                value={formData.observaciones}
                onChange={handleInputChange}
                className="input"
                rows={3}
                placeholder="Observaciones adicionales"
              />
            </div>
          </div>
        </div>

        {/* Botones de Acción */}
        <div className="flex justify-end space-x-4">
          <button type="button" className="btn btn-secondary">
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary">
            <FileText className="w-4 h-4 mr-2" />
            Procesar Matrícula
          </button>
        </div>
      </form>
    </div>
  );
};

export default MatriculaInicial;
